import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react";
import cn from "classnames";
import { CloseIcon } from "@/shared/assets/icons";
import { LEAVE_SCHEDULE_TYPES, LEAVE_SCHEDULE_TYPE_COLORS } from "@/shared/consts";
import type { LeaveScheduleItem, LeaveScheduleType } from "@/shared/types";
import { CALENDAR_LOCALE } from "@/shared/ui/DatePicker/locale";
import type { Locale } from "@/shared/utils/types";

export interface LeaveScheduleModalProps {
  locale: Locale;
  items: LeaveScheduleItem[];
  title: string;
  getLeaveTypeLabel: (leaveType: LeaveScheduleType) => string;
  formatMoreLeaves: (count: number) => string;
  onClose: () => void;
}

const MAX_VISIBLE_LEAVES = 2;

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
}

function getShortName(fullName: string): string {
  const parts = fullName.split(" ");
  if (parts.length < 2) return fullName;
  if (parts.length >= 3) return `${parts[0]} ${parts[1][0]}. ${parts[2][0]}.`;
  return `${parts[0]} ${parts[1][0]}.`;
}

export default function LeaveScheduleModal({
  locale,
  items,
  title,
  getLeaveTypeLabel,
  formatMoreLeaves,
  onClose,
}: LeaveScheduleModalProps) {
  const calendarLocale = CALENDAR_LOCALE[locale];
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const headerTitle = `${calendarLocale.months[month]} ${year}`;

  function handlePrev() {
    setCurrentDate(new Date(year, month - 1, 1));
  }

  function handleNext() {
    setCurrentDate(new Date(year, month + 1, 1));
  }

  const leavesByDate = useMemo(() => {
    const map = new Map<string, LeaveScheduleItem[]>();

    for (const leave of items) {
      const start = parseLocalDate(leave.start_date);
      const end = parseLocalDate(leave.end_date);
      const cursor = new Date(start);

      while (cursor <= end) {
        const key = toDateKey(cursor);
        const existing = map.get(key);
        if (existing) {
          existing.push(leave);
        } else {
          map.set(key, [leave]);
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return map;
  }, [items]);

  const gridDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days: Date[] = [];

    for (let i = startOffset - 1; i >= 0; i -= 1) {
      days.push(new Date(year, month, -i));
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push(new Date(year, month, day));
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let day = 1; day <= remaining; day += 1) {
        days.push(new Date(year, month + 1, day));
      }
    }

    return days;
  }, [year, month]);

  const today = new Date();

  return createPortal(
    <div
      className="fixed top-0 left-0 w-screen h-screen surface-backdrop blur-level-1 flex justify-center items-center z-[10200]"
      onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="background-static-white radius-lg prompt-box-shadow relative flex flex-col w-6xl max-h-[85vh] p-8 gap-4"
        onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="btn btn-tertiary btn-icon absolute! top-8 right-8 z-10 w-8! h-8! p-0! flex items-center justify-center radius-xs content-base-secondary! cursor-pointer"
          onClick={onClose}>
          <CloseIcon />
        </button>

        <h2 className="text-display-xs content-base-primary pr-12">{title}</h2>

        <div className="flex items-center justify-between">
          <span className="text-body-bold-md content-base-primary">{headerTitle}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrev}
              className="btn btn-tertiary btn-icon w-8! h-8! p-0! flex items-center justify-center radius-xs cursor-pointer">
              <ArrowLeft2 size={18} color="currentColor" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn btn-tertiary btn-icon w-8! h-8! p-0! flex items-center justify-center radius-xs cursor-pointer">
              <ArrowRight2 size={18} color="currentColor" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="grid grid-cols-7">
            {calendarLocale.weekdays.map((weekday) => (
              <div
                key={weekday}
                className="py-2 text-center text-label-xs content-base-low border-b surface-component-stroke">
                {weekday}
              </div>
            ))}
          </div>

          <div
            className={cn(
              "grid grid-cols-7 flex-1 border-r border-b surface-component-stroke overflow-y-auto component-scroll",
              gridDays.length === 42 ? "grid-rows-6" : "grid-rows-5",
            )}>
            {gridDays.map((day) => {
              const isCurrentMonth = day.getMonth() === month;
              const isToday = isSameDay(day, today);
              const key = toDateKey(day);
              const leaves = leavesByDate.get(key) ?? [];
              const uniqueLeaves = leaves.filter(
                (leave, index, allLeaves) => allLeaves.findIndex((candidate) => candidate.id === leave.id) === index,
              );
              const visibleLeaves = uniqueLeaves.slice(0, MAX_VISIBLE_LEAVES);
              const overflow = uniqueLeaves.length - MAX_VISIBLE_LEAVES;

              return (
                <div
                  key={key}
                  className={cn(
                    "min-h-20 p-1 border-t border-l surface-component-stroke flex flex-col",
                    !isCurrentMonth && "surface-tertiary-fill",
                  )}>
                  <div className="flex justify-end mb-0.5">
                    <span
                      className={cn(
                        "text-label-xs w-6 h-6 flex items-center justify-center",
                        isToday && "background-brand-fill text-white radius-full",
                        isCurrentMonth && !isToday && "content-base-primary",
                        !isCurrentMonth && "content-base-low",
                      )}>
                      {day.getDate()}
                    </span>
                  </div>

                  {visibleLeaves.length > 0 && (
                    <div className="flex-1 flex flex-col gap-0.5">
                      {visibleLeaves.map((leave) => (
                        <div
                          key={leave.id}
                          title={`${leave.worker_name} - ${getLeaveTypeLabel(leave.leave_type)}`}
                          className={cn(
                            "px-1 py-0.5 radius-2xs text-body-regular-xs truncate",
                            LEAVE_SCHEDULE_TYPE_COLORS[leave.leave_type] ?? "background-neutral-subtle",
                          )}>
                          {getShortName(leave.worker_name)}
                        </div>
                      ))}

                      {overflow > 0 && (
                        <span className="text-body-regular-2xs content-base-low px-1">{formatMoreLeaves(overflow)}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-x-5 gap-y-1 mt-2">
          {LEAVE_SCHEDULE_TYPES.map((type) => (
            <div key={type} className="flex items-center gap-1.5">
              <div className={cn("w-2.5 h-2.5 radius-2xs shrink-0", LEAVE_SCHEDULE_TYPE_COLORS[type])} />
              <span className="text-label-xs content-base-secondary">{getLeaveTypeLabel(type)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
